/**
 * Web stub for the `@tauri-apps/*` packages.
 *
 * `app/tauri/tauriCompat.js` (loaded via `bootTauri.web.js`) statically imports
 * several Tauri V2 modules — `@tauri-apps/api/{core,event,webviewWindow,app}`
 * and the `@tauri-apps/plugin-{store,os,process,http,log}` plugins. Those
 * packages are NOT installed in this app, so the plain web / Cloudflare Pages
 * build (webpack) fails with `Can't resolve '@tauri-apps/api/core'`.
 *
 * By design, none of those APIs run in a plain browser: `initTauriCompat()`
 * detects the absence of `window.__TAURI__` and returns after installing no-op
 * shims, so every real Tauri call is unreachable off-desktop. The imports
 * therefore only need to *resolve* at build time. This single module is aliased
 * for all of the above specifiers in `webpack.config.js` and exports every
 * named symbol the compat layer imports as a harmless no-op.
 *
 * A genuine Tauri desktop build bundles the real packages with its own
 * toolchain and never goes through this webpack web config, so it is unaffected.
 */

const noop = () => {};
const asyncNoop = async () => {};

/**
 * Build a Proxy whose every property is an async no-op function, so any method
 * call on a returned handle (window controls, store handle, …) is safe even
 * though these are never invoked in a browser context.
 * @returns {object}
 */
const noopHandle = () =>
  new Proxy(
    {},
    {
      get: () => asyncNoop,
    },
  );

// --- @tauri-apps/api/core -------------------------------------------------
export const invoke = asyncNoop;
export const convertFileSrc = (p) => p;

// --- @tauri-apps/api/event ------------------------------------------------
// listen() resolves to an unlisten function.
export const listen = async () => noop;
export const once = async () => noop;
export const emit = asyncNoop;

// --- @tauri-apps/api/webviewWindow ---------------------------------------
export const getCurrentWebviewWindow = () => noopHandle();

// --- @tauri-apps/api/app --------------------------------------------------
export const getVersion = async () => '0.0.0-web';
export const getName = async () => 'happy-services-app';
export const getTauriVersion = async () => '0.0.0-web';

// --- @tauri-apps/plugin-store --------------------------------------------
// load() resolves to a Store handle; entries() must be iterable.
export const load = async () => ({
  entries: async () => [],
  get: async () => undefined,
  set: async () => {},
  delete: async () => {},
  save: async () => {},
  clear: async () => {},
  keys: async () => [],
  values: async () => [],
});

// --- @tauri-apps/plugin-os (namespace import) -----------------------------
export const platform = () => 'web';
export const type = () => 'web';
export const arch = () => '';
export const version = () => '';
export const hostname = async () => '';
export const locale = async () => null;

// --- @tauri-apps/plugin-process ------------------------------------------
export const relaunch = asyncNoop;
export const exit = asyncNoop;

// --- @tauri-apps/plugin-http ---------------------------------------------
// Fall back to the browser's native fetch so any accidental call still works.
export const fetch = (...args) =>
  (typeof globalThis !== 'undefined' && typeof globalThis.fetch === 'function'
    ? globalThis.fetch(...args)
    : Promise.reject(new Error('fetch is not available')));

// --- @tauri-apps/plugin-log (namespace import) ----------------------------
export const warn = noop;
export const debug = noop;
export const trace = noop;
export const info = noop;
export const error = noop;
export const attachConsole = asyncNoop;
export const attachLogger = asyncNoop;

export default {};
